import { useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Search } from 'react-feather';
import { useHotkeys } from 'react-hotkeys-hook';

import { colors } from '../ui-elements/Main/MainTheme';
import { Text } from '../ui-elements/Main/Text';
import { useHover } from '@/helpers/app/hooks';
import { useViewport } from '@/lib/layout/dimensions';
import { SearchModal } from './SearchModal';

export const SearchButton = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const isMobile = useViewport((width) => width < 600, 500);
  const ref = useRef<HTMLDivElement>(null);

  useHover(ref, setHover);

  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Global keyboard shortcut to open search (Cmd+K or Ctrl+K)
  useHotkeys('mod+k', (e) => {
    e.preventDefault();
    openModal();
  }, { enableOnFormTags: true });

  return (
    <>
      <Wrap
        ref={ref}
        onClick={openModal}
        aria-label="Search"
      >
        <Search size={20} />
        {!isMobile && hover && (
          <Tooltip
            display='flex'
            fontSize="14px"
            color={colors.white}
          >
            Search
            <Kbd>K</Kbd>
          </Tooltip>
        )}
      </Wrap>
      <SearchModal isOpen={isModalOpen} onClose={closeModal} />
    </>
  );
};

const Wrap = styled.div`
  position: relative;
  cursor: pointer;
  margin-right: 10px;
  background: ${colors.sand};
  padding: 5px;
  height: 36px;
  width: 36px;
  border: 1px solid rgba(224, 198, 183, 0.2);
  border-radius: 50%;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    stroke: ${colors.darkGreen};
  }

  &:hover {
    background: ${colors.sand50};
  }
`;

const Tooltip = styled(Text)`
  background: black;
  padding: 12px 20px;
  border-radius: 12px;
  position: absolute;
  z-index: 1;
  white-space: nowrap;
  min-width: fit-content;
  right: 50%;
  top: 100%;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Kbd = styled.kbd`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  padding: 2px 6px;
  font-family: 'SF Mono', Monaco, 'Courier New', monospace;
  font-size: 11px;
  font-weight: 600;
  color: white;

  &::before {
    content: '\u2318';
    margin-right: 2px;
  }
`;

export default SearchButton;
